import { useMemo } from 'react';
import type { ValidationIssue } from '../../../../types';
import { buildValidationMaps } from '../utils';

export interface ValidationIssueLookup {
  /** All issues (field- and line-level) bucketed for a line. */
  getLineIssues: (lineIndex: number) => ValidationIssue[];
  /** Worst severity among the line's issues; `null` when none. */
  getLineSeverity: (lineIndex: number) => 'error' | 'warning' | null;
  fieldHasIssue: (lineIndex: number, field: string) => boolean;
  fieldHasError: (lineIndex: number, field: string) => boolean;
  /** Worst issue (by severity) for a field, or `undefined` when none. Returned
   * verbatim so renderers (`ErrorEntry`) can format with `meta.expected` /
   * `meta.actual`. */
  fieldWorstIssue: (lineIndex: number, field: string) => ValidationIssue | undefined;
  /** Issues with an empty `path` — rendered as a top-level error banner. */
  topLevelIssues: ValidationIssue[];
}

const EMPTY_ISSUES: ValidationIssue[] = [];
const fieldKey = (lineIndex: number, field: string) => `${lineIndex}|${field}`;

/**
 * Memoized accessors over a flat list of `ValidationIssue`s, bucketed by
 * `invoiceLines.<index>[.<field>]`. Issues outside the `invoiceLines.*` namespace (other
 * than `path === ''`, which surfaces as `topLevelIssues`) are ignored.
 */
export function useValidationIssueLookup(
  errors: readonly ValidationIssue[]
): ValidationIssueLookup {
  // Memoized as a whole so the lookup is a stable prop for memoized line rows.
  return useMemo(() => {
    const { lineIssuesMap, fieldIssuesMap, lineSeverityMap, fieldWorstMap } =
      buildValidationMaps(errors);
    return {
      getLineIssues: lineIndex => lineIssuesMap.get(lineIndex) ?? EMPTY_ISSUES,
      getLineSeverity: lineIndex => lineSeverityMap.get(lineIndex) ?? null,
      fieldHasIssue: (lineIndex, field) => fieldIssuesMap.has(fieldKey(lineIndex, field)),
      fieldHasError: (lineIndex, field) =>
        fieldWorstMap.get(fieldKey(lineIndex, field))?.hasError ?? false,
      fieldWorstIssue: (lineIndex, field) => fieldWorstMap.get(fieldKey(lineIndex, field))?.issue,
      topLevelIssues: errors.filter(issue => issue.path === '')
    } satisfies ValidationIssueLookup;
  }, [errors]);
}
